/* eslint-disable max-lines-per-function */
import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import removeIcon from '../../../Assets/images/png/remove-icon.png';
import SearchDropdown from '../../../Components/Dropdown/dropdown';
import { MANAGE_KPI_DASHBOARD } from '../../../Constants/strings';
import styles from './dynamicInput.module.scss';

const DynamicInput = ({
  categoryId,
  inputChangeHandler,
  inputRowList,
  removeRow,
  kpiList,
  targetFormatList,
  selectedKpis,
  setSelectedKpis,
}) => {
  const [symbolsList, setSymbolsList] = useState([]);
  const [yesNoList, setYesNoList] = useState([]);

  useEffect(() => {
    if (inputRowList && inputRowList.length > 0) {
      inputRowList.forEach((inputRow) => {
        const selectedTargetFormat = targetFormatList.find(format => format.format === inputRow.targetFormatId);
        if (selectedTargetFormat) {
          if (selectedTargetFormat.name === 'Yes/No') {
            setYesNoList(selectedTargetFormat.options);
          } else {
            setSymbolsList(selectedTargetFormat.options);
          }
        }
      });
    }
  }, [inputRowList, targetFormatList]);

  const handleKpiChange = useCallback(
    (id, value) => {
      inputChangeHandler({
        rowId: id,
        key: 'kpiId',
        value,
      });
    },
    [inputChangeHandler],
  );

  const handleTargetFormatChange = useCallback(
    (id, value) => {
      inputChangeHandler({
        rowId: id,
        key: 'targetFormatId',
        value,
      });
      const selectedTargetFormat = targetFormatList.find(format => format.id === value.id);
      if (selectedTargetFormat) {
        if (selectedTargetFormat.name === 'Yes/No') {
          setYesNoList(selectedTargetFormat.options);
        } else {
          setSymbolsList(selectedTargetFormat.options);
        }
      }
    },
    [inputChangeHandler, targetFormatList],
  );

  const handleSymbolChange = useCallback(
    (id, value) => {
      inputChangeHandler({
        rowId: id,
        key: 'symbol',
        value,
      });
    },
    [inputChangeHandler],
  );

  const handleSymbolTargetValueChange = useCallback(
    (id, value) => {
      const re = /^\d+(\.\d{0,2})?$/;
      if (value === '' || re.test(value)) {
        inputChangeHandler({
          rowId: id,
          key: 'symbolTargetValue',
          value,
        });
      }
    },
    [inputChangeHandler],
  );

  const handleTargetValueChange = useCallback(
    (id, value) => {
      inputChangeHandler({
        rowId: id,
        key: 'targetValue',
        value,
      });
    },
    [inputChangeHandler],
  );

  const renderSymbolInput = (id, symbol, symbolTargetValue, targetFormatId) => (
    <div className={styles['target-is-container']}>
      <SearchDropdown
        options={symbolsList}
        dropdownCss={[styles.dropdown, styles['symbol-dropdown']].join(' ')}
        searchBox={false}
        value={symbol}
        disabled={targetFormatId === ''}
        onChange={(event) => handleSymbolChange(id, event.target.value)}
      />
      <TextField
        size="small"
        disabled={targetFormatId === ''}
        value={symbolTargetValue}
        className={styles.textbox}
        onChange={(event) => handleSymbolTargetValueChange(id, event.target.value)}
      />
    </div>
  );

  const renderYesNoInput = (id, targetValue) => (
    <div className={styles['target-is-container']}>
      <SearchDropdown
        options={yesNoList}
        dropdownCss={[styles.dropdown, styles['yes-no-dropdown']].join(' ')}
        searchBox={false}
        value={targetValue}
        placeholder={MANAGE_KPI_DASHBOARD.SET.SELECT_PLACEHOLDER.YES_NO}
        onChange={(event) => handleTargetValueChange(id, event.target.value)}
      />
      <TextField
        size="small"
        disabled= {true}
        value={''}
        className={styles.textbox}
        style={{visibility: 'hidden'}}
      />
    </div>
  );

  const handleRemoveRow = useCallback((rowId, kpiCategoryId) => {
    removeRow(rowId);
    setSelectedKpis((prevSelectedKpis) =>
      prevSelectedKpis.filter((kpi) => !(kpi.rowId === rowId && kpi.categoryId === kpiCategoryId)),
    );
  }, [removeRow, setSelectedKpis]);

  const renderTargetInput = (id, symbol, symbolTargetValue, targetValue, targetFormatId) => {
    const isObject = typeof targetFormatId === 'object';
    const formatName = isObject ? targetFormatId.name : targetFormatId;
    if (formatName === 'Yes/No') {
      return renderYesNoInput(id, targetValue);
    }
    return renderSymbolInput(id, symbol, symbolTargetValue, targetFormatId);
  };

  return (
    <div>
      {inputRowList?.map((inputRow) => {
        const {
          id,
          kpiId,
          targetFormatId,
          symbol,
          symbolTargetValue,
          targetValue,
        } = inputRow;
        // Filter kpiList to exclude already selected values
        const filteredKpiList = kpiList.filter(
          (kpi) => !selectedKpis.some((selectedKpi) => (selectedKpi.value.id === kpi.id
            && (kpiId.id ? kpiId.id !== selectedKpi.value.id : kpiId !== selectedKpi.value.name)
          )),
        );

        return (
          <React.Fragment key={id}>
            <div className={styles['input-row']}>
              <div className={styles['kpi-dropdown-container']}>
                <SearchDropdown
                  options={filteredKpiList}
                  dropdownCss={[styles.dropdown, styles['kpi-dropdown']].join(' ')}
                  searchBox={false}
                  value={kpiId}
                  placeholder={MANAGE_KPI_DASHBOARD.SET.SELECT_PLACEHOLDER.KPI}
                  onChange={(event) => handleKpiChange(id, event.target.value)}
                />
              </div>
              <div>
                <div className={[styles['input-container'], styles['target-format-dropdown-container']].join(' ')}>
                  <label>{MANAGE_KPI_DASHBOARD.SET.LABEL.TARGET_FORMAT}</label>
                  <SearchDropdown
                    options={targetFormatList}
                    dropdownCss={[styles.dropdown, styles['target-format-dropdown']].join(' ')}
                    searchBox={false}
                    value={targetFormatId}
                    disabled={kpiId === ''}
                    placeholder={MANAGE_KPI_DASHBOARD.SET.SELECT_PLACEHOLDER.TARGET_FORMAT}
                    onChange={(event) => handleTargetFormatChange(id, event.target.value)}
                  />
                </div>
              </div>
              <div>
                <div className={styles['input-container']}>
                  <label>{MANAGE_KPI_DASHBOARD.SET.LABEL.TARGET_IS}</label>
                  {renderTargetInput(id, symbol, symbolTargetValue, targetValue, targetFormatId)}
                  <button type="button" className={styles['remove-btn']}
                    onClick={() => handleRemoveRow(id, categoryId)}>
                    <img src={removeIcon} alt="remove icon" />
                  </button>
                </div>
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

DynamicInput.propTypes = {
  categoryId: PropTypes.number.isRequired,
  inputChangeHandler: PropTypes.func.isRequired,
  inputRowList: PropTypes.array.isRequired,
  removeRow: PropTypes.func.isRequired,
  kpiList: PropTypes.array.isRequired,
  targetFormatList: PropTypes.array.isRequired,
  selectedKpis: PropTypes.array.isRequired, // Receive selected KPIs
  setSelectedKpis: PropTypes.func,
};

export default DynamicInput;
